import { useEffect, useState } from "react";
import { X, Trash2, Plus, Minus, ChevronRight, Upload, Copy, Check, ExternalLink, ShoppingBag, Gift, AlertCircle, Truck, Building2, Banknote } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError, formatCLP } from "../lib/api";
import { toast } from "sonner";
import GiftThermometer from "./GiftThermometer";

const STEPS = ["cart", "form", "payment", "success"];
const MIN_FOR_REWARD = 10000;

export default function CartDrawer() {
    const { items, remove, setQty, total, clear, open, setOpen, pendingReward, setPendingReward } = useCart();
    const { user, refresh } = useAuth();
    const [step, setStep] = useState("cart");
    const [submitting, setSubmitting] = useState(false);
    const [copied, setCopied] = useState(false);
    const [paymentSettings, setPaymentSettings] = useState({
        payment_link: "",
        payment_instructions: "",
        bank_name: "",
        bank_account_type: "",
        bank_account_number: "",
        bank_account_holder: "",
        bank_rut: "",
        bank_email: "",
    });
    const [receiptData, setReceiptData] = useState(""); // base64 data url
    const [receiptName, setReceiptName] = useState("");
    const [branches, setBranches] = useState([]);
    const [deliveryType, setDeliveryType] = useState("shipping"); // "pickup" | "shipping"
    const [pickupBranchId, setPickupBranchId] = useState("");
    const [showBank, setShowBank] = useState(false);
    const [form, setForm] = useState({
        full_name: "",
        rut: "",
        phone: "",
        email: "",
        address: "",
        notes: "",
    });

    // Reset checkout to cart step + load payment settings only when drawer transitions open
    useEffect(() => {
        if (open) {
            setStep("cart");
            setShowBank(false);
            api.get("/settings/payment-link")
                .then(({ data }) => setPaymentSettings(data))
                .catch(() => {});
            api.get("/branches")
                .then(({ data }) => {
                    const active = (data || []).filter((b) => b.active);
                    setBranches(active);
                    if (active.length && !pickupBranchId) setPickupBranchId(active[0].id);
                })
                .catch(() => {});
        }
    }, [open]);

    // Prefill form from user once available, without resetting step mid-flow
    useEffect(() => {
        if (user && user.id) {
            setForm((f) => ({
                ...f,
                full_name: f.full_name || user.name || "",
                email: f.email || user.email || "",
                phone: f.phone || user.phone || "",
            }));
        }
    }, [user]);

    const onCopyTotal = async () => {
        try {
            await navigator.clipboard.writeText(String(Math.round(total)));
            setCopied(true);
            toast.success("Monto copiado al portapapeles");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("No se pudo copiar");
        }
    };

    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("La imagen no puede superar los 5MB");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setReceiptData(reader.result);
            setReceiptName(file.name);
        };
        reader.readAsDataURL(file);
    };

    const validateForm = () => {
        const required = deliveryType === "pickup"
            ? ["full_name", "rut", "phone", "email"]
            : ["full_name", "rut", "phone", "email", "address"];
        for (const k of required) {
            if (!form[k]?.trim()) {
                toast.error("Completa todos los campos obligatorios");
                return false;
            }
        }
        if (deliveryType === "pickup" && !pickupBranchId) {
            toast.error("Selecciona la sucursal donde retirarás");
            return false;
        }
        return true;
    };

    const submitOrder = async () => {
        if (!receiptData) {
            toast.error("Sube el comprobante de pago");
            return;
        }
        if (pendingReward && total < MIN_FOR_REWARD) {
            toast.error(`Para canjear puntos tu pedido debe ser de ${formatCLP(MIN_FOR_REWARD)} o más.`);
            return;
        }
        if (pendingReward && !user?.id) {
            toast.error("Debes iniciar sesión para canjear puntos");
            return;
        }
        setSubmitting(true);
        try {
            const customer = { ...form };
            if (deliveryType === "pickup") {
                const b = branches.find((x) => x.id === pickupBranchId);
                customer.address = customer.address || `Retiro en sucursal ${b?.name || ""} (${b?.address || ""})`;
            }
            const body = {
                items,
                customer,
                receipt_base64: receiptData,
                total,
                delivery_type: deliveryType,
                pickup_branch_id: deliveryType === "pickup" ? pickupBranchId : null,
                branch_id: pickupBranchId || (branches[0]?.id),
            };
            if (pendingReward) body.redeemed_reward_id = pendingReward.reward_id;
            await api.post("/orders", body);
            setStep("success");
            clear();
            if (user?.id) {
                try { await refresh(); } catch {}
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50" data-testid="cart-drawer">
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={() => setOpen(false)}
            />
            <aside className="absolute inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl flex flex-col animate-fade-up">
                <header className="flex items-center justify-between p-4 border-b border-border">
                    <div className="flex items-center gap-2">
                        <ShoppingBag className="w-5 h-5 text-cocoa" />
                        <h2 className="font-display font-semibold text-lg">
                            {step === "cart" && "Tu carrito"}
                            {step === "form" && "Tus datos"}
                            {step === "payment" && "Pagar"}
                            {step === "success" && "¡Pedido enviado!"}
                        </h2>
                    </div>
                    <button
                        data-testid="cart-close-btn"
                        onClick={() => setOpen(false)}
                        className="p-2 rounded-full hover:bg-muted"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto">
                    {step === "cart" && (
                        <div className="p-4 space-y-3">
                            {pendingReward && (
                                <div className="bg-gradient-to-r from-marigold/20 to-mexyellow/30 border-2 border-marigold rounded-2xl p-3 flex items-center gap-3" data-testid="pending-reward-banner">
                                    {pendingReward.photo_base64 && (
                                        <img src={pendingReward.photo_base64} alt={pendingReward.name} className="w-14 h-14 object-cover rounded-xl" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[10px] uppercase tracking-widest font-bold text-marigold flex items-center gap-1">
                                            <Gift className="w-3 h-3" /> Canje con puntos
                                        </div>
                                        <div className="font-semibold text-sm text-cocoa truncate">{pendingReward.name}</div>
                                        <div className="text-xs text-cocoa/70">{pendingReward.points_cost} pts</div>
                                    </div>
                                    <button
                                        onClick={() => { setPendingReward(null); toast.info("Canje quitado"); }}
                                        className="text-cocoa hover:bg-white/60 rounded-full p-1.5"
                                        title="Quitar canje"
                                        data-testid="remove-pending-reward"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                            {pendingReward && total < MIN_FOR_REWARD && (
                                <div className="bg-mexred/10 border border-mexred/40 rounded-2xl p-3 text-xs text-cocoa flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-mexred" />
                                    <span>Para canjear puntos tu pedido debe ser de <strong>{formatCLP(MIN_FOR_REWARD)}</strong> o más. Te faltan <strong>{formatCLP(MIN_FOR_REWARD - total)}</strong>.</span>
                                </div>
                            )}
                            {items.length > 0 && <GiftThermometer total={total} />}
                            {items.length === 0 ? (
                                <div className="py-16 text-center text-muted-foreground">
                                    <p className="font-display text-lg text-cocoa font-semibold">Tu carrito está esperando 🌶️</p>
                                    <p className="text-sm mt-1">¡Agrega tus antojitos favoritos y vamos por el regalo sorpresa!</p>
                                </div>
                            ) : (
                                items.map((it) => (
                                    <div
                                        key={it.product_id}
                                        data-testid={`cart-item-${it.product_id}`}
                                        className="flex gap-3 bg-muted/30 rounded-2xl p-3"
                                    >
                                        <div className="w-16 h-16 rounded-xl bg-white overflow-hidden shrink-0">
                                            {it.image && (
                                                <img
                                                    src={it.image}
                                                    alt={it.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-sm line-clamp-1">{it.name}</div>
                                            <div className="text-cocoa font-bold text-sm">{formatCLP(it.price)}</div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <button
                                                    onClick={() => setQty(it.product_id, it.quantity - 1)}
                                                    className="w-7 h-7 rounded-full bg-white border border-border flex items-center justify-center hover:bg-mexred hover:text-cocoa transition-colors"
                                                >
                                                    <Minus className="w-3 h-3" />
                                                </button>
                                                <span className="text-sm w-6 text-center">{it.quantity}</span>
                                                <button
                                                    onClick={() => setQty(it.product_id, it.quantity + 1)}
                                                    className="w-7 h-7 rounded-full bg-white border border-border flex items-center justify-center hover:bg-mexred hover:text-cocoa transition-colors"
                                                >
                                                    <Plus className="w-3 h-3" />
                                                </button>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => remove(it.product_id)}
                                            className="text-muted-foreground hover:text-cocoa"
                                            data-testid={`remove-cart-${it.product_id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {step === "form" && (
                        <div className="p-4 space-y-3">
                            {/* Selector de tipo de entrega */}
                            <div className="bg-mexyellow/20 border border-mexyellow rounded-2xl p-3">
                                <div className="text-sm font-semibold text-cocoa mb-2">¿Cómo quieres recibir tu pedido?</div>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setDeliveryType("shipping")}
                                        data-testid="delivery-shipping"
                                        className={`p-3 rounded-xl border-2 transition-all text-sm font-semibold flex flex-col items-center gap-1 ${
                                            deliveryType === "shipping"
                                                ? "bg-mexred border-mexred text-cocoa shadow-md"
                                                : "bg-white border-border text-cocoa hover:border-mexred"
                                        }`}
                                    >
                                        <Truck className="w-5 h-5" />
                                        Envío a domicilio
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setDeliveryType("pickup")}
                                        data-testid="delivery-pickup"
                                        className={`p-3 rounded-xl border-2 transition-all text-sm font-semibold flex flex-col items-center gap-1 ${
                                            deliveryType === "pickup"
                                                ? "bg-mexred border-mexred text-cocoa shadow-md"
                                                : "bg-white border-border text-cocoa hover:border-mexred"
                                        }`}
                                    >
                                        <Building2 className="w-5 h-5" />
                                        Retiro en tienda
                                    </button>
                                </div>
                                {deliveryType === "pickup" && branches.length > 0 && (
                                    <div className="mt-3">
                                        <label className="text-xs font-semibold text-cocoa">Selecciona la sucursal:</label>
                                        <div className="space-y-2 mt-1">
                                            {branches.map((b) => (
                                                <label key={b.id} className={`flex items-start gap-2 p-2 rounded-xl border cursor-pointer ${
                                                    pickupBranchId === b.id ? "border-mexred bg-mexred/10" : "border-border bg-white hover:border-mexred"
                                                }`}>
                                                    <input
                                                        type="radio"
                                                        checked={pickupBranchId === b.id}
                                                        onChange={() => setPickupBranchId(b.id)}
                                                        className="mt-1"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-sm">{b.name}</div>
                                                        <div className="text-xs text-muted-foreground">{b.address}</div>
                                                        {b.phone && <div className="text-xs text-muted-foreground">📱 {b.phone}</div>}
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {[
                                { name: "full_name", label: "Nombre y apellido *", placeholder: "Juan Pérez" },
                                { name: "rut", label: "RUT *", placeholder: "12.345.678-9" },
                                { name: "phone", label: "Teléfono *", placeholder: "+56 9 ..." },
                                { name: "email", label: "Correo *", placeholder: "tu@correo.cl", type: "email" },
                                ...(deliveryType === "shipping" ? [{ name: "address", label: "Dirección completa *", placeholder: "Calle, número, comuna" }] : []),
                            ].map((f) => (
                                <div key={f.name}>
                                    <label className="text-sm font-medium text-cocoa">{f.label}</label>
                                    <input
                                        data-testid={`checkout-${f.name}`}
                                        type={f.type || "text"}
                                        value={form[f.name]}
                                        onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                                        placeholder={f.placeholder}
                                        className="mt-1 w-full px-3 py-2.5 rounded-xl border border-border bg-white focus:border-mexred focus:ring-2 focus:ring-mexred/20 outline-none text-sm"
                                    />
                                </div>
                            ))}
                            <div>
                                <label className="text-sm font-medium text-cocoa">Observaciones</label>
                                <textarea
                                    data-testid="checkout-notes"
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    placeholder="Preferencias, horario de entrega, etc."
                                    className="mt-1 w-full px-3 py-2.5 rounded-xl border border-border bg-white focus:border-mexred focus:ring-2 focus:ring-mexred/20 outline-none text-sm min-h-20"
                                />
                            </div>
                        </div>
                    )}

                    {step === "payment" && (
                        <div className="p-4 space-y-4">
                            <div className="bg-mexyellow/30 border border-mexyellow rounded-2xl p-4 text-sm">
                                <p className="font-display font-semibold text-cocoa mb-2 text-base">
                                    ¡Ya casi son tuyos! 🎉
                                </p>
                                <p className="mb-3">Estás a un paso de finalizar tu compra.</p>
                                <ol className="list-decimal list-inside space-y-1 text-cocoa/90">
                                    <li>Presiona <strong>"Copiar monto"</strong> para pagar correctamente.</li>
                                    <li>Realiza el pago en el link.</li>
                                    <li>Toma una captura del comprobante.</li>
                                    <li>Súbelo aquí para completar tu pedido.</li>
                                </ol>
                            </div>

                            <div className="bg-white border border-border rounded-2xl p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Monto a pagar</span>
                                    <span className="font-display text-2xl font-bold text-cocoa">
                                        {formatCLP(total)}
                                    </span>
                                </div>
                                <button
                                    data-testid="copy-amount-btn"
                                    onClick={onCopyTotal}
                                    className="w-full bg-cocoa text-white rounded-full py-2.5 font-semibold flex items-center justify-center gap-2 hover:bg-cocoa/90 transition-colors"
                                >
                                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    {copied ? "Copiado" : "Copiar monto"}
                                </button>
                                {paymentSettings.payment_link ? (
                                    <a
                                        data-testid="payment-link-btn"
                                        href={paymentSettings.payment_link}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="w-full bg-marigold text-cocoa rounded-full py-2.5 font-semibold flex items-center justify-center gap-2 hover:bg-marigold/90 transition-colors"
                                    >
                                        <ExternalLink className="w-4 h-4" /> Ir al link de pago
                                    </a>
                                ) : (
                                    <p className="text-xs text-muted-foreground text-center">
                                        Pronto disponible el link de pago. Contáctanos por WhatsApp.
                                    </p>
                                )}
                                {(paymentSettings.bank_name || paymentSettings.bank_account_number) && (
                                    <button
                                        type="button"
                                        data-testid="pay-transfer-btn"
                                        onClick={() => setShowBank(true)}
                                        className="w-full bg-flaggreen text-white rounded-full py-2.5 font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-colors"
                                    >
                                        <Banknote className="w-4 h-4" /> Pagar con transferencia
                                    </button>
                                )}
                                {paymentSettings.payment_instructions && (
                                    <p className="text-xs text-cocoa/70">{paymentSettings.payment_instructions}</p>
                                )}
                            </div>

                            {/* Modal de datos bancarios */}
                            {showBank && (
                                <div className="bg-white border-2 border-flaggreen rounded-2xl p-4 space-y-2 relative" data-testid="bank-details-modal">
                                    <button
                                        type="button"
                                        onClick={() => setShowBank(false)}
                                        className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                    <div className="flex items-center gap-2 font-display font-semibold text-cocoa">
                                        <Banknote className="w-5 h-5 text-flaggreen" /> Datos para transferencia
                                    </div>
                                    <div className="text-sm space-y-1 bg-muted/30 rounded-xl p-3">
                                        {paymentSettings.bank_name && <div><strong>Banco:</strong> {paymentSettings.bank_name}</div>}
                                        {paymentSettings.bank_account_type && <div><strong>Tipo cuenta:</strong> {paymentSettings.bank_account_type}</div>}
                                        {paymentSettings.bank_account_number && (
                                            <div className="flex items-center gap-2">
                                                <strong>Nº cuenta:</strong> {paymentSettings.bank_account_number}
                                                <button
                                                    onClick={() => { navigator.clipboard.writeText(paymentSettings.bank_account_number); toast.success("Copiado"); }}
                                                    className="text-xs px-2 py-0.5 rounded-md bg-cocoa text-white"
                                                >Copiar</button>
                                            </div>
                                        )}
                                        {paymentSettings.bank_account_holder && <div><strong>Titular:</strong> {paymentSettings.bank_account_holder}</div>}
                                        {paymentSettings.bank_rut && <div><strong>RUT:</strong> {paymentSettings.bank_rut}</div>}
                                        {paymentSettings.bank_email && <div><strong>Email:</strong> {paymentSettings.bank_email}</div>}
                                    </div>
                                    <div className="bg-mexyellow/30 border border-mexyellow rounded-xl p-2.5 text-xs text-cocoa">
                                        📸 <strong>NOTA:</strong> recuerda hacer un capture de pantalla de la transferencia y adjuntarlo abajo en "Subir comprobante de pago".
                                    </div>
                                    <button
                                        type="button"
                                        data-testid="bank-done-btn"
                                        onClick={() => setShowBank(false)}
                                        className="w-full bg-cocoa text-white rounded-full py-2 font-semibold mt-2"
                                    >
                                        Listo
                                    </button>
                                </div>
                            )}

                            <div className="bg-white border-2 border-dashed border-mexred/40 rounded-2xl p-4">
                                <label className="block">
                                    <span className="text-sm font-semibold text-cocoa block mb-2">
                                        Subir comprobante de pago *
                                    </span>
                                    <div className="flex items-center justify-center w-full p-6 rounded-xl bg-mexred/5 hover:bg-mexred/10 transition-colors cursor-pointer">
                                        <div className="text-center">
                                            <Upload className="w-6 h-6 text-cocoa mx-auto mb-2" />
                                            <p className="text-sm text-cocoa">
                                                {receiptName ? receiptName : "Haz click para subir imagen"}
                                            </p>
                                            <p className="text-xs text-muted-foreground">PNG / JPG · máx 5MB</p>
                                        </div>
                                    </div>
                                    <input
                                        data-testid="receipt-input"
                                        type="file"
                                        accept="image/*"
                                        onChange={onFile}
                                        className="hidden"
                                    />
                                </label>
                                {receiptData && (
                                    <img
                                        src={receiptData}
                                        alt="Comprobante"
                                        className="mt-3 max-h-40 mx-auto rounded-xl shadow"
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {step === "success" && (
                        <div className="p-6 text-center space-y-4">
                            <img
                                src="/logo.png"
                                alt="La Dulcería Mexicana"
                                className="w-32 h-32 mx-auto rounded-full shadow-xl ring-4 ring-mexyellow animate-wobble"
                            />
                            <h3 className="font-display text-2xl font-semibold text-cocoa">
                                ¡Pedido recibido con éxito! ✅
                            </h3>
                            <p className="text-cocoa font-medium">
                                ¡Gracias por confiar en <strong className="text-cocoa">La Dulcería Mexicana</strong>! 🌶️
                            </p>
                            <p className="text-muted-foreground text-sm">
                                Nos contactaremos en las próximas horas para coordinar tu entrega.
                            </p>
                            <p className="text-sm text-cocoa">
                                Si necesitas ayuda inmediata, escríbenos por <strong>WhatsApp</strong>.
                            </p>
                            <a
                                href="https://wa.me/56998091369"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-6 py-2.5 font-semibold"
                            >
                                Abrir WhatsApp
                            </a>
                        </div>
                    )}
                </div>

                <footer className="border-t border-border p-4 space-y-3 bg-white">
                    {step !== "success" && (
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Total</span>
                            <span className="font-display text-xl font-bold text-cocoa">
                                {formatCLP(total)}
                            </span>
                        </div>
                    )}

                    {step === "cart" && (
                        <button
                            data-testid="cart-next-btn"
                            disabled={items.length === 0 || (pendingReward && total < MIN_FOR_REWARD)}
                            onClick={() => setStep("form")}
                            className="w-full bg-mexred hover:bg-mexred_hover disabled:bg-muted disabled:text-muted-foreground text-cocoa rounded-full py-3 font-semibold flex items-center justify-center gap-2 transition-all shadow-md"
                        >
                            ¡Lo quiero ya! <ChevronRight className="w-4 h-4" />
                        </button>
                    )}

                    {step === "form" && (
                        <div className="flex gap-2">
                            <button
                                onClick={() => setStep("cart")}
                                className="px-4 py-3 rounded-full border border-border font-medium text-cocoa hover:bg-muted"
                            >
                                Atrás
                            </button>
                            <button
                                data-testid="form-next-btn"
                                onClick={() => validateForm() && setStep("payment")}
                                className="flex-1 bg-mexred hover:bg-mexred_hover text-cocoa rounded-full py-3 font-semibold flex items-center justify-center gap-2 transition-all shadow-md"
                            >
                                Continuar al pago <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {step === "payment" && (
                        <div className="flex gap-2">
                            <button
                                onClick={() => setStep("form")}
                                className="px-4 py-3 rounded-full border border-border font-medium text-cocoa hover:bg-muted"
                            >
                                Atrás
                            </button>
                            <button
                                data-testid="submit-order-btn"
                                disabled={submitting}
                                onClick={submitOrder}
                                className="flex-1 bg-marigold hover:bg-marigold/90 disabled:bg-muted text-cocoa rounded-full py-3 font-semibold flex items-center justify-center gap-2 transition-all shadow-md"
                            >
                                {submitting ? "Enviando..." : "Enviar pedido"}
                            </button>
                        </div>
                    )}

                    {step === "success" && (
                        <button
                            onClick={() => setOpen(false)}
                            className="w-full bg-cocoa text-white rounded-full py-3 font-semibold"
                        >
                            Cerrar
                        </button>
                    )}
                </footer>
            </aside>
        </div>
    );
}
